import { useCallback, useEffect, useRef } from 'react'

interface RetryConfig {
  maxRetries: number
  retryDelay: number
  backoffFactor: number
}

const DEFAULT_CONFIG = {
  debounceTime: 1000,
  retryConfig: {
    maxRetries: 3,
    retryDelay: 1000,
    backoffFactor: 1.5
  }
}

const createRetryHandler = (
  callback: () => Promise<void>,
  config: RetryConfig,
  isMounted: React.MutableRefObject<boolean>
) => {
  let retryCount = 0
  let retryTimeoutRef: NodeJS.Timeout | null = null

  const clearRetryTimeout = () => {
    if (retryTimeoutRef) {
      clearTimeout(retryTimeoutRef)
      retryTimeoutRef = null
    }
  }

  const handleWithRetry = async () => {
    if (!isMounted.current) return

    try {
      await callback()
      retryCount = 0 // Reset retry count on success
    } catch (error) {
      console.error('Error in retry handler:', error)

      if (retryCount < config.maxRetries) {
        const delay = config.retryDelay * Math.pow(config.backoffFactor, retryCount)
        retryCount += 1

        clearRetryTimeout()
        retryTimeoutRef = setTimeout(() => {
          void handleWithRetry()
        }, delay)
      }
    }
  }

  return {
    execute: handleWithRetry,
    clear: clearRetryTimeout
  }
}

export const useSyncWithRetry = (
  syncOperation: () => Promise<void>,
  id: string,
  config = DEFAULT_CONFIG
) => {
  const isMounted = useRef(true)
  const lastActiveTimestamp = useRef(Date.now())
  const retryHandler = useRef(createRetryHandler(syncOperation, config.retryConfig, isMounted))

  useEffect(() => {
    retryHandler.current = createRetryHandler(syncOperation, config.retryConfig, isMounted)
  }, [syncOperation, config.retryConfig])

  const maybeRetry = useCallback(
    (source: string) => {
      const timeSinceLastActive = Date.now() - lastActiveTimestamp.current
      if (isMounted.current && timeSinceLastActive > config.debounceTime) {
        // console.log(`[useSyncWithRetry] ${source} → retry triggered`)
        void retryHandler.current.execute()
      }
      lastActiveTimestamp.current = Date.now()
    },
    [config.debounceTime]
  )

  const handleVisibilityChange = useCallback(() => {
    if (document.visibilityState === 'visible') {
      maybeRetry('visibilitychange')
    }
  }, [maybeRetry])

  const handleFocus = useCallback(() => {
    maybeRetry('focus')
  }, [maybeRetry])

  const handlePageShow = useCallback(() => {
    maybeRetry('pageshow')
  }, [maybeRetry])

  const handleBlur = useCallback(() => {
    // console.log('[useSyncWithRetry] blur → clear retries')

    // Clear any pending retries when window loses focus
    lastActiveTimestamp.current = Date.now()
    retryHandler.current.clear()
  }, [])

  const handleOnline = useCallback(() => {
    if (document.visibilityState === 'visible') {
      maybeRetry('online')
    }
  }, [maybeRetry])

  useEffect(() => {
    void retryHandler.current.execute()
  }, [id])

  useEffect(() => {
    isMounted.current = true

    // The visibility state of the document changes (e.g., tab switch, minimize, or restore browser).
    document.addEventListener('visibilitychange', handleVisibilityChange)

    // The window/tab gains focus (user interacts or switches back to the tab).
    window.addEventListener('focus', handleFocus)

    // The window/tab loses focus (user clicks away, switches tabs, or minimizes the browser).
    window.addEventListener('blur', handleBlur)

    // The browser detects that the device has reconnected to the internet after being offline.
    window.addEventListener('online', handleOnline)

    // The page is shown again after being hidden (e.g., navigating back to the tab).
    window.addEventListener('pageshow', handlePageShow)

    return () => {
      isMounted.current = false
      retryHandler.current.clear()

      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handleFocus)
      window.removeEventListener('blur', handleBlur)
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('pageshow', handlePageShow)
    }
  }, [handleVisibilityChange, handleFocus, handleBlur, handleOnline, handlePageShow])
}
